/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, { useContext, useEffect } from 'react';

import { USE_COOKIES_BANNER } from '../../constants';
import { useGlobalState } from '../../state/globalStateContext';
import { BackgroundColorTheme } from '../../templates/Page';
import { CalendlyEventName, pushCalendlyEvent, withDataLayer } from '../../tracking/dataLayer';
import {
  useActivateGoogleOptimize,
  useDimensions,
  useScrollY,
  useStoreReferrerOnLoad,
  useStoreUTMParamsOnLoad,
} from '../../utils/hooks';
import { clsx } from '../../utils/utils';
import CookiesBanner from './CookiesBanner';
import Footer, { FooterProps } from './Footer';
import Header, { HeaderProps } from './Header';
import * as styles from './Layout.module.scss';

interface LayoutProps {
  pageTheme?: BackgroundColorTheme;
  children: React.ReactNode;
  headerConfig: HeaderProps;
  footerConfig: FooterProps;
  cookiesBannerConfig?: {
    textContent?: React.ReactNode;
  };
  hideNavigation?: boolean;
  heroRef?: React.MutableRefObject<HTMLElement | null>;
}

const Layout = ({
  pageTheme,
  children,
  headerConfig,
  footerConfig,
  cookiesBannerConfig,
  hideNavigation,
  heroRef,
}: LayoutProps): React.ReactElement => {
  const isHidden = useActivateGoogleOptimize();
  const { setIsEmbedded } = useGlobalState();

  useStoreUTMParamsOnLoad();
  useStoreReferrerOnLoad();

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const sanityPreviewParam = urlSearchParams.get('_sanityPreview');

    setIsEmbedded(window.parent !== window && !sanityPreviewParam);

    window.addEventListener('message', function (e) {
      // Listen for Calendly events
      const eventName: string | undefined = e?.data?.event;
      if (e.origin === 'https://calendly.com' && eventName?.startsWith('calendly.')) {
        withDataLayer(pushCalendlyEvent(eventName as CalendlyEventName));
      }
    });
  }, []);

  const { height: heroHeight } =
    (heroRef && useDimensions(heroRef, { measureOnScroll: false })) || {};
  const scrollY = useScrollY();
  const headerWithOpaqueBackgroundFromHeroScroll =
    !!scrollY && !!heroHeight && scrollY > heroHeight;

  const headerConfigWithHeroScroll = {
    ...headerConfig,
    withOpaqueBackground:
      headerConfig.withOpaqueBackground || headerWithOpaqueBackgroundFromHeroScroll,
  };

  return (
    <div
      className={clsx(
        styles.container,
        headerConfig.withOpaqueBackground && styles.headerWithOpaqueBackground,
      )}
      style={isHidden ? { opacity: 0 } : undefined}
    >
      <Header
        {...headerConfigWithHeroScroll}
        hideNavigation={hideNavigation}
      ></Header>
      <div role="main" className={clsx(styles.main)} data-theme={'light'}>
        {children}
      </div>
      <Footer {...footerConfig} hideNavigation={hideNavigation}></Footer>
      {USE_COOKIES_BANNER && cookiesBannerConfig?.textContent && (
        <CookiesBanner textContent={cookiesBannerConfig.textContent} />
      )}
    </div>
  );
};

export default Layout;
